import { EPrimitiveKind, IPlanPrimitive } from '../plan-primitive';
import { EPaneId, EPaneRole, IPane, SHEET_TITLE_BAND, scaleText, standardDenominator } from '../sheet-layout';
import {
    EDxfEntityKind,
    EDxfHorizontalAlign,
    EDxfVerticalAlign,
    IDxfBounds,
    IDxfEntity,
    IDxfPoint,
    IDxfText,
    boundsOfPoints,
    entitiesBounds,
    growBounds,
    isEmptyBounds,
    textCorners,
    textWidth,
    translateBounds,
    translateEntity,
    unionBounds,
} from './dxf-entities';
import { IDxfPaneFit, PAPER_MM_PER_PX, paneDenominator, toPaneMm } from './dxf-fit';
import { EDxfLayerKind, paneLayerName } from './dxf-layers';
import { paneEntities } from './dxf-primitives';
import { IDxfSegment, IDxfSketch, boxesMeet, segmentHitsBox, sketchOf } from './dxf-sketch';
import { toDxfText } from './dxf-text';

/** Height of a view title on paper, millimetres (ГОСТ 2.304 size 3.5): it fits the sheet's 5.8 mm title row. */
export const DXF_TITLE_TEXT_MM: number = 3.5;
/** The narrowest a title is squeezed to stay inside its pane. */
const MIN_TITLE_WIDTH_FACTOR: number = 0.6;
/** Where a title starts in its pane, pixels: a detail's title is indented inside its cell, as on the screen. */
const DETAIL_TITLE_INDENT_PX: number = 8;
/** Air a detail view keeps inside its cell on every side, on paper, millimetres. */
const DETAIL_MARGIN_MM: number = 0.5;
/** Air between a detail's title row and the top of its drawing, on paper, millimetres. */
const DETAIL_TOP_AIR_MM: number = 2;
/** A cell height no detail reaches, sheet pixels: the cell a detail's own need is measured in. */
const UNBOUNDED_CELL_PX: number = 100_000;
/** How many standard scales down a detail view may go to fit its dimensions. */
const MAX_SCALE_STEPS: number = 2;
/** Nudges a denominator past itself when looking for the next standard one. */
const SCALE_STEP_EPSILON: number = 0.01;
/**
 * Air a projection view's title keeps from its drawing, on paper, millimetres. The text boxes are
 * estimated at cap height; a CAD font box reaches past it by its ascent and descent, so the air
 * covers both.
 */
const TITLE_AIR_MM: number = 2;
/** The step a title slides along its row, and a window lifts by, looking for a free spot, on paper, millimetres. */
const TITLE_STEP_MM: number = 1;
/** Space kept between two views in model space, millimetres. */
const PANE_GAP_MM: number = 1000;
/**
 * The highest Y the model drawing reaches. The sheet in paper space spans 0…594 × 0…420 mm; a
 * viewer that draws paper space over model space (LibreCAD does) then finds it clear of the views.
 */
const MODEL_TOP_MM: number = -1000;

/** One view placed in model space: its entities and the window its viewport shows. */
export interface IPlacedView {
    pane: IPane;
    /** The N of «1 : N» the view is drawn and annotated at. */
    denominator: number;
    entities: IDxfEntity[];
    /** The pane's rectangle on the sheet, title row included, in model millimetres. */
    window: IDxfBounds;
}

/** A view in its own millimetres, before it takes its place: what it draws and the room it needs. */
interface ILocalView extends IPlacedView {
    /** The window and everything drawn past it: no other view may come inside. */
    box: IDxfBounds;
    /** How far the window rose over the drawing to clear the title, model millimetres. */
    lift: number;
}

/** A title clear of its view's drawing and how far the window rose for it. */
interface ITitleSpot {
    title: IDxfText;
    lift: number;
}

/** A detail view laid out at one scale, and how much of its cell it takes. */
interface ILaidDetail {
    view: ILocalView;
    overflow: number;
    /** The drawing with its dimensions, the title left out. */
    content: IDxfBounds;
}

/** Where a view's title starts, how wide its row is and at what scale it is set. */
interface ITitlePlace {
    at: IDxfPoint;
    room: number;
    scale: number;
}

/** A view title: the name, then the scale label, squeezed to the room of its row. */
function titleText(pane: IPane, scaleLabel: string | null, place: ITitlePlace): IDxfText {
    const label: string = toDxfText(scaleLabel === null ? pane.title.toUpperCase() : `${pane.title.toUpperCase()}  ${scaleLabel}`);
    const height: number = DXF_TITLE_TEXT_MM * place.scale;
    const widthFactor: number = Math.max(MIN_TITLE_WIDTH_FACTOR, Math.min(1, place.room / textWidth(label, height)));
    return {
        kind: EDxfEntityKind.Text,
        layer: paneLayerName(pane.id, EDxfLayerKind.Text),
        ltScale: place.scale,
        at: place.at,
        text: label,
        rotationDeg: 0,
        hAlign: EDxfHorizontalAlign.Left,
        vAlign: EDxfVerticalAlign.Middle,
        height,
        widthFactor,
    };
}

/** The title of a view in its title row: the name, then the scale, squeezed to the pane's width. */
export function titleEntity(pane: IPane, fit: IDxfPaneFit): IDxfText {
    const indent: number = pane.role === EPaneRole.Detail ? DETAIL_TITLE_INDENT_PX : 0;
    return titleText(pane, pane.scale, {
        at: toPaneMm(fit, [indent, SHEET_TITLE_BAND / 2]),
        room: (pane.rect.w - 2 * indent) / fit.pxPerMm,
        scale: fit.scale,
    });
}

/** The next standard scale after «1 : N». */
function nextDenominator(denominator: number): number {
    return standardDenominator(1 / (PAPER_MM_PER_PX * denominator * (1 + SCALE_STEP_EPSILON)));
}

/**
 * A detail view laid out at «1 : N»: its dimensions in rows around its parts, the drawing centred
 * across its cell and right under the title row, so the title stands over its drawing however
 * tall the cell. `overflow` is how much of the cell the drawing with its dimensions takes: 1 or
 * less keeps clear of the cell's edges.
 */
function detailAt(pane: IPane, pxPerMm: number, denominator: number, scaleLabel: string | null): ILaidDetail {
    const fit: IDxfPaneFit = { pxPerMm, origin: pane.projection.origin, paneId: pane.id, scale: denominator };
    const mmPerPx: number = PAPER_MM_PER_PX * denominator;
    const width: number = pane.rect.w * mmPerPx;
    const height: number = (pane.rect.h - SHEET_TITLE_BAND) * mmPerPx;
    const band: number = SHEET_TITLE_BAND * mmPerPx;
    const margin: number = DETAIL_MARGIN_MM * denominator;
    const entities: IDxfEntity[] = paneEntities(pane.projection.primitives, fit, {
        width: width - 2 * margin,
        height: height - 2 * margin,
    });
    const content: IDxfBounds = entitiesBounds(entities);
    const empty: boolean = isEmptyBounds(content);
    const cx: number = empty ? 0 : (content.minX + content.maxX) / 2;
    const cy: number = empty ? 0 : (content.minY + content.maxY) / 2;
    const top: number = empty ? height / 2 : Math.min(cy + height / 2, content.maxY + DETAIL_TOP_AIR_MM * denominator);
    const window: IDxfBounds = { minX: cx - width / 2, maxX: cx + width / 2, minY: top - height, maxY: top + band };
    const indent: number = DETAIL_TITLE_INDENT_PX * mmPerPx;
    const title: IDxfText = titleText(pane, scaleLabel, {
        at: [window.minX + indent, window.maxY - band / 2],
        room: width - 2 * indent,
        scale: denominator,
    });
    const all: IDxfEntity[] = [...entities, title];
    const overflow: number = empty
        ? 0
        : Math.max((content.maxX - content.minX) / (width - 2 * margin), (content.maxY - content.minY) / (height - 2 * margin));
    return {
        view: { pane, denominator, window, entities: all, box: unionBounds(window, entitiesBounds(all)), lift: 0 },
        overflow,
        content,
    };
}

/**
 * The cell height, sheet pixels, a detail needs to keep its sheet scale: its drawing with its
 * dimension rows, the air around it and the title row. Null for a detail without a millimetre
 * scale: it keeps its cell.
 */
function detailNeed(pane: IPane): number | null {
    const pxPerMm: number | null = pane.projection.pxPerMm;
    if (pane.scale === null || pxPerMm === null || pxPerMm <= 0) {
        return null;
    }
    const denominator: number = paneDenominator(pxPerMm);
    // Laid out in a cell of no height limit, the parts keep the places the sheet gave them.
    const laid: ILaidDetail = detailAt({ ...pane, rect: { ...pane.rect, h: UNBOUNDED_CELL_PX } }, pxPerMm, denominator, pane.scale);
    if (isEmptyBounds(laid.content)) {
        return null;
    }
    const paper: number = (laid.content.maxY - laid.content.minY) / denominator + 2 * DETAIL_MARGIN_MM + DETAIL_TOP_AIR_MM;
    return Math.ceil(paper / PAPER_MM_PER_PX) + SHEET_TITLE_BAND;
}

/** The new tops of the detail rows when each takes `heights`, the gaps between them kept. */
function rowTops(rows: readonly number[], oldHeights: readonly number[], heights: readonly number[]): number[] {
    const tops: number[] = [];
    rows.forEach((row: number, index: number): void => {
        const previous: number = index - 1;
        tops.push(index === 0 ? row : tops[previous] + heights[previous] + (row - rows[previous] - oldHeights[previous]));
    });
    return tops;
}

/**
 * Row heights for a zone too short for every row's need: a row that needs less than its cell keeps
 * what it needs, and the rest goes to the rows short of theirs, in proportion to the shortage.
 */
function shareShortZone(heights: readonly number[], needs: readonly number[]): number[] {
    const spare: number = heights.reduce((sum: number, height: number, i: number): number => sum + Math.max(0, height - needs[i]), 0);
    const shortage: number = heights.reduce((sum: number, height: number, i: number): number => sum + Math.max(0, needs[i] - height), 0);
    return heights.map((height: number, i: number): number =>
        needs[i] <= height ? Math.ceil(needs[i]) : Math.floor(height + (spare * (needs[i] - height)) / shortage)
    );
}

/**
 * The detail rows of the sheet re-shared for the file. A detail's dimensions in their ГОСТ rows
 * are paper sizes, larger than the screen draws them, so a detail can outgrow its cell at the
 * scale the sheet gave it while the next row has room to spare. The zone's height is then shared
 * by what each row needs at its scale, the spare in proportion, and the detail keeps its scale
 * instead of stepping down. Rows that fit already keep the sheet's cells; in a zone too short for
 * every row's need, a row with room to spare gives it to the rows short of theirs.
 */
export function fitDetailRows(panes: readonly IPane[]): IPane[] {
    const details: IPane[] = panes.filter((pane: IPane): boolean => pane.role === EPaneRole.Detail);
    const rows: number[] = [...new Set(details.map((pane: IPane): number => pane.rect.y))].sort((a: number, b: number): number => a - b);
    const inRow: (row: number) => IPane[] = (row: number): IPane[] => details.filter((pane: IPane): boolean => pane.rect.y === row);
    const heights: number[] = rows.map((row: number): number => Math.max(...inRow(row).map((pane: IPane): number => pane.rect.h)));
    const needs: number[] = rows.map((row: number): number =>
        Math.max(...inRow(row).map((pane: IPane): number => detailNeed(pane) ?? pane.rect.h))
    );
    const room: number = heights.reduce((sum: number, height: number): number => sum + height, 0);
    const need: number = needs.reduce((sum: number, height: number): number => sum + height, 0);
    if (needs.every((height: number, index: number): boolean => height <= heights[index])) {
        return [...panes];
    }
    const shared: number[] =
        need > room
            ? shareShortZone(heights, needs)
            : needs.map((height: number): number => Math.floor(height + ((room - need) * height) / need));
    const tops: number[] = rowTops(rows, heights, shared);
    return panes.map((pane: IPane): IPane => {
        const index: number = pane.role === EPaneRole.Detail ? rows.indexOf(pane.rect.y) : -1;
        return index < 0 ? pane : { ...pane, rect: { ...pane.rect, y: tops[index], h: shared[index] } };
    });
}

/**
 * A detail view at its sheet scale or, when its dimensions in their ГОСТ rows do not fit the
 * cell at that scale, at the next standard one that they do; the title then names the new scale.
 */
function detailView(pane: IPane, pxPerMm: number): ILocalView {
    let denominator: number = paneDenominator(pxPerMm);
    let best: ILaidDetail = detailAt(pane, pxPerMm, denominator, pane.scale);
    const dimensioned: boolean = pane.projection.primitives.some((prim: IPlanPrimitive): boolean => prim.kind === EPrimitiveKind.Dim);
    for (let step: number = 0; step < MAX_SCALE_STEPS && best.overflow > 1 && dimensioned && pane.scale !== null; step++) {
        denominator = nextDenominator(denominator);
        const laid: ILaidDetail = detailAt(pane, pxPerMm, denominator, scaleText(denominator));
        // A smaller scale is taken only when it helps: the rows are paper sizes and do not shrink.
        best = laid.overflow < best.overflow ? laid : best;
    }
    return best.view;
}

/** Whether a title, with its air, touches a line or a label of the view's drawing. */
function titleBlocked(title: IDxfText, sketch: IDxfSketch, air: number): boolean {
    const box: IDxfBounds = growBounds(boundsOfPoints(textCorners(title)), air);
    return (
        sketch.segments.some((segment: IDxfSegment): boolean => segmentHitsBox(segment, box)) ||
        sketch.labels.some((label: IDxfBounds): boolean => boxesMeet(label, box))
    );
}

/**
 * The title in the top row of a window, slid right from the window's left edge past whatever of
 * the drawing reaches the row (a room wall, a dimension); null when no spot of the row is free
 * before the title would have to be squeezed.
 */
function titleInRow(pane: IPane, fit: IDxfPaneFit, sketch: IDxfSketch, window: IDxfBounds): IDxfText | null {
    const step: number = TITLE_STEP_MM * fit.scale;
    const air: number = TITLE_AIR_MM * fit.scale;
    const y: number = window.maxY - SHEET_TITLE_BAND / fit.pxPerMm / 2;
    const at: (x: number) => IDxfText = (x: number): IDxfText =>
        titleText(pane, pane.scale, { at: [x, y], room: window.maxX - x, scale: fit.scale });
    let title: IDxfText = at(window.minX);
    while (titleBlocked(title, sketch, air)) {
        title = at(title.at[0] + step);
        if (title.widthFactor < 1) {
            return null;
        }
    }
    return title;
}

/**
 * A projection view's title, clear of its drawing: in the window's top row where a free spot is
 * found, else with the window lifted over the drawing as far as the drawing's bottom allows. The
 * lift starts from `minLift`, which the views sharing a row on the sheet take in common.
 */
function placeTitle(pane: IPane, fit: IDxfPaneFit, drawing: readonly IDxfEntity[], window: IDxfBounds, minLift: number): ITitleSpot {
    const sketch: IDxfSketch = sketchOf(drawing);
    const content: IDxfBounds = entitiesBounds(drawing);
    const spare: number = isEmptyBounds(content) ? 0 : content.minY - window.minY;
    for (let lift: number = minLift; lift <= Math.max(minLift, spare); lift += TITLE_STEP_MM * fit.scale) {
        const title: IDxfText | null = titleInRow(pane, fit, sketch, translateBounds(window, 0, lift));
        if (title !== null) {
            return { title, lift };
        }
    }
    const fallback: IDxfText = titleEntity(pane, fit);
    return { title: { ...fallback, at: [fallback.at[0], fallback.at[1] + minLift] }, lift: minLift };
}

/** A projection view: its drawing at the sheet scale and its title clear of it. */
function projectionView(pane: IPane, pxPerMm: number, minLift: number): ILocalView {
    const denominator: number = paneDenominator(pxPerMm);
    const fit: IDxfPaneFit = { pxPerMm, origin: pane.projection.origin, paneId: pane.id, scale: denominator };
    const drawing: IDxfEntity[] = paneEntities(pane.projection.primitives, fit);
    const sheetWindow: IDxfBounds = boundsOfPoints([toPaneMm(fit, [0, 0]), toPaneMm(fit, [pane.rect.w, pane.rect.h])]);
    const spot: ITitleSpot = placeTitle(pane, fit, drawing, sheetWindow, minLift);
    const window: IDxfBounds = translateBounds(sheetWindow, 0, spot.lift);
    const entities: IDxfEntity[] = [...drawing, spot.title];
    return { pane, denominator, entities, window, box: unionBounds(window, entitiesBounds(entities)), lift: spot.lift };
}

/** A pane with a millimetre scale in its own millimetres; null for a pane without one (the dial). */
function localView(pane: IPane, minLift: number = 0): ILocalView | null {
    const pxPerMm: number | null = pane.projection.pxPerMm;
    if (pxPerMm === null || pxPerMm <= 0) {
        return null;
    }
    return pane.role === EPaneRole.Detail ? detailView(pane, pxPerMm) : projectionView(pane, pxPerMm, minLift);
}

/**
 * The front and the side view stand on one floor line on the sheet: a window lifted to clear the
 * title of one is lifted as far for the other, so the line stays one.
 */
function sharedRowLift(views: readonly ILocalView[]): ILocalView[] {
    const row: ReadonlySet<EPaneId> = new Set<EPaneId>([EPaneId.FrontView, EPaneId.SideView]);
    const lifts: number[] = views.filter((view: ILocalView): boolean => row.has(view.pane.id)).map((view: ILocalView): number => view.lift);
    const lift: number = Math.max(0, ...lifts);
    return views.map((view: ILocalView): ILocalView =>
        row.has(view.pane.id) && view.lift < lift ? (localView(view.pane, lift) ?? view) : view
    );
}

function moved(view: ILocalView, dx: number, dy: number): ILocalView {
    return {
        ...view,
        entities: view.entities.map((entity: IDxfEntity): IDxfEntity => translateEntity(entity, dx, dy)),
        window: translateBounds(view.window, dx, dy),
        box: translateBounds(view.box, dx, dy),
    };
}

function overlaps(a: IDxfBounds, b: IDxfBounds, gap: number): boolean {
    return a.minX < b.maxX + gap && b.minX < a.maxX + gap && a.minY < b.maxY + gap && b.minY < a.maxY + gap;
}

/**
 * The projection group keeps its arrangement: the views share one scale, so each goes where the
 * sheet put it, its pixels divided by that scale — the side view on the front view's floor line,
 * the top view under the front view on its X. A view whose drawing reaches past its pane (the
 * room walls of the top view) moves away from its neighbours along the line that keeps the
 * alignment: the side view right, the top view down.
 */
function placeGroup(views: readonly ILocalView[]): ILocalView[] {
    const placed: ILocalView[] = [];
    const order: EPaneId[] = [EPaneId.FrontView, EPaneId.SideView, EPaneId.TopView];
    const sorted: ILocalView[] = [...views].sort(
        (a: ILocalView, b: ILocalView): number => order.indexOf(a.pane.id) - order.indexOf(b.pane.id)
    );
    for (const view of sorted) {
        const pxPerMm: number = view.pane.projection.pxPerMm ?? 1;
        let at: ILocalView = moved(
            view,
            (view.pane.rect.x + view.pane.projection.origin.x) / pxPerMm,
            -(view.pane.rect.y + view.pane.projection.origin.y) / pxPerMm
        );
        for (const other of placed) {
            // Boxes that only touch keep the sheet's arrangement: neither window then sees the other's drawing.
            if (!overlaps(at.box, other.box, 0)) {
                continue;
            }
            at =
                view.pane.id === EPaneId.TopView
                    ? moved(at, 0, other.box.minY - PANE_GAP_MM - at.box.maxY)
                    : moved(at, other.box.maxX + PANE_GAP_MM - at.box.minX, 0);
        }
        placed.push(at);
    }
    return placed;
}

/**
 * Detail views take their places below the group in the sheet's own order: the rows of the
 * sheet top to bottom, left to right inside a row, kept apart by a fixed gap.
 */
function placeDetails(views: readonly ILocalView[], left: number, top: number): ILocalView[] {
    const rows: number[] = [...new Set(views.map((view: ILocalView): number => view.pane.rect.y))].sort(
        (a: number, b: number): number => a - b
    );
    const placed: ILocalView[] = [];
    let rowTop: number = top;
    for (const row of rows) {
        const inRow: ILocalView[] = views
            .filter((view: ILocalView): boolean => view.pane.rect.y === row)
            .sort((a: ILocalView, b: ILocalView): number => a.pane.rect.x - b.pane.rect.x);
        let x: number = left;
        let height: number = 0;
        for (const view of inRow) {
            const at: ILocalView = moved(view, x - view.box.minX, rowTop - view.box.maxY);
            placed.push(at);
            x = at.box.maxX + PANE_GAP_MM;
            height = Math.max(height, at.box.maxY - at.box.minY);
        }
        rowTop -= height + PANE_GAP_MM;
    }
    return placed;
}

/**
 * The views with a millimetre scale laid out in model space, 1 : 1 in millimetres: the
 * projection group as the sheet arranges it, the details below it, each view's box clear of
 * every other, so a viewport on one view never shows a piece of another.
 */
export function placeViews(panes: readonly IPane[]): IPlacedView[] {
    const local: ILocalView[] = sharedRowLift(
        panes
            .map((pane: IPane): ILocalView | null => localView(pane))
            .filter((view: ILocalView | null): view is ILocalView => view !== null)
    );
    const group: ILocalView[] = placeGroup(local.filter((view: ILocalView): boolean => view.pane.role === EPaneRole.Projection));
    const groupBox: IDxfBounds = group.reduce((box: IDxfBounds, view: ILocalView): IDxfBounds => unionBounds(box, view.box), {
        minX: 0,
        minY: 0,
        maxX: 0,
        maxY: 0,
    });
    const details: ILocalView[] = placeDetails(
        local.filter((view: ILocalView): boolean => view.pane.role !== EPaneRole.Projection),
        group.length > 0 ? groupBox.minX : 0,
        group.length > 0 ? groupBox.minY - PANE_GAP_MM : 0
    );
    const all: ILocalView[] = [...group, ...details];
    const top: number = Math.max(...all.map((view: ILocalView): number => view.box.maxY));
    const lift: number = Number.isFinite(top) ? Math.min(0, MODEL_TOP_MM - top) : 0;
    return all.map((view: ILocalView): IPlacedView => {
        const shifted: ILocalView = moved(view, 0, lift);
        return { pane: shifted.pane, denominator: shifted.denominator, entities: shifted.entities, window: shifted.window };
    });
}
